import dayjs from 'dayjs'
import { Key, useCallback, useState } from 'react'
import { Pressable } from '@react-aria/interactions'
import { Button } from 'blaze-ui/Button'
import { Text } from 'blaze-ui/Text/Text'
import { ArrowRefresh2, Edit3, Plus, Trash2, Upload5 } from 'blaze-ui/icons/20'
import { Close } from 'blaze-ui/icons/16'
import { DateTimePicker } from 'blaze-ui/DateTimePicker/DateTimePicker'
import { Input } from 'almanac-ui/TextInputField/Input'
import { Skeleton } from 'components/Skeleton/Skeleton'
import { BrandKitAvatar, CampaignOption, ReferenceMediaFile } from 'types'
import { AvatarPickerModal } from 'components/AvatarPickerModal/AvatarPickerModal'
import { useHasInRotationAvatars } from 'hooks/queries/brandKits/useHasInRotationAvatars'
import { MissingAvatarsCallout } from 'components/MissingAvatarsCallout/MissingAvatarsCallout'
import { ContentTypeSelect } from 'screens/Schedule/PostPromptsSection/ContentTypeSelect'
import {
  CONTENT_CATEGORY_KEY_TO_CONTENT_CATEGORY,
  CONTENT_CATEGORY_TO_CONTENT_CATEGORY_KEY,
  ContentCategoryKey,
} from 'screens/Schedule/PostPromptsSection/utils'
import { getMediaTypesForCategory } from 'utils/brandPlans/contentCategories'
import { ReferenceImageTrigger } from 'screens/Schedule/PostPromptsSection/ReferenceImageTrigger'
import { ReferenceImageButton } from 'screens/Schedule/PostPromptsSection/ReferenceImageButton'
import { isVideoReferenceMedia } from 'screens/Schedule/PostPromptsSection/referenceMedia'
import { ReferenceImgLoading } from 'screens/Schedule/PostPromptsSection/ReferenceImg'
import { useIsStrictBrandControl } from 'hooks/useIsStrictBrandControl'
import { useModals } from 'almanac-ui/Modal'
import { useDeleteFloatingContextSource } from 'hooks/queries/schedules/useDeleteFloatingContextSource'
import { CONTENT_CATEGORY_CONFIG } from 'utils/brandPlans/contentCategories'
import { getDefaultTacticsForPostType } from '../CreateContentModal/utils'
import { AddContextModal } from './AddContextModal'
import { AvatarScriptSettings, ScriptSettingsModal } from './ScriptSettingsModal'
import { ComposeRowData, ContextSourceEntry } from './Types'
import { useVideoScriptSupport } from 'hooks/queries/videos/useVideoScriptSupport'
import { isVideoScriptCategory } from 'utils/videoScriptRequest'
import Styles from './ComposeRow.module.scss'
import { getSourceLabel, SourceIcon } from 'screens/ContentPlan/CreateCampaignModalV2/AddContext'
import { Pill } from 'components/Pill/Pill'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import cx from 'classnames'
import { useBrandProfile } from 'hooks/queries/workspaces/useBrandProfile'

interface ComposeRowProps {
  row: ComposeRowData
  schedule: CampaignOption
  brandKitId?: number
  defaultCaptionStyle?: string
  defaultVoiceoverVoice?: string
  onUpdate: (id: string, updates: Partial<ComposeRowData>) => void
  onRemove: (id: string) => void
  onRegenerate?: (id: string) => void
  onRegenerateWithContext?: (id: string, contextSourceIds: number[]) => void
  onRetryReferenceImage?: (id: string) => void
  canRemove: boolean
}

export const ComposeRow = ({
  row,
  schedule,
  brandKitId,
  defaultCaptionStyle,
  defaultVoiceoverVoice,
  onUpdate,
  onRemove,
  onRegenerate,
  onRegenerateWithContext,
  onRetryReferenceImage,
  canRemove,
}: ComposeRowProps) => {
  const isStrictBrandControl = useIsStrictBrandControl()
  const { openModal, closeModal } = useModals()
  const { hasInRotationAvatars, isLoading: isLoadingAvatars } = useHasInRotationAvatars({ brandKitId })
  const isAvatarRow = row.contentCategory === 'avatar_video' && !row.isBlank
  const isVideoScriptRow = !row.isBlank && isVideoScriptCategory(row.contentCategory)
  const {
    supported: videoScriptSupported,
    token: videoScriptToken,
    requestParams: videoScriptRequest,
  } = useVideoScriptSupport({
    brandPlanScheduleId: schedule.id,
    row,
  })
  const showScriptSettings = isAvatarRow || (isVideoScriptRow && videoScriptSupported === true)
  const avatarRowDisabled = isAvatarRow && !isLoadingAvatars && !hasInRotationAvatars

  const handleOpenScriptSettings = useCallback(() => {
    if (!brandKitId) return
    if (isAvatarRow) {
      openModal(ScriptSettingsModal, {
        mode: 'avatar',
        value: {
          avatar: row.avatar,
          avatarAngle: row.avatarAngle,
          avatarScript: row.avatarScript,
          avatarCaptions: row.avatarCaptions,
          avatarCaptionStyle: row.avatarCaptionStyle,
          videoVoiceoverVoice: row.videoVoiceoverVoice,
          productImage: row.productImage,
        },
        topic: row.topic,
        brandKitId,
        defaultCaptionStyle,
        onSubmit: (settings: AvatarScriptSettings, nextTopic: string) => {
          onUpdate(row.id, { ...settings, topic: nextTopic })
          closeModal()
        },
      })
      return
    }

    if (!videoScriptRequest) return
    openModal(ScriptSettingsModal, {
      mode: 'ugc_video',
      value: {
        avatar: null,
        avatarAngle: row.avatarAngle,
        avatarScript: row.videoScript,
        avatarCaptions: row.avatarCaptions,
        avatarCaptionStyle: row.avatarCaptionStyle,
        videoVoiceoverVoice: row.videoVoiceoverVoice,
        productImage: null,
      },
      topic: row.topic,
      brandKitId,
      brandPlanScheduleId: schedule.id,
      videoScriptRequest,
      initialScriptToken: videoScriptToken,
      defaultCaptionStyle,
      defaultVoiceoverVoice,
      onSubmit: (settings: AvatarScriptSettings, nextTopic: string) => {
        onUpdate(row.id, {
          videoScript: settings.avatarScript,
          avatarCaptions: settings.avatarCaptions,
          avatarCaptionStyle: settings.avatarCaptionStyle,
          videoVoiceoverVoice: settings.videoVoiceoverVoice,
          topic: nextTopic,
        })
        closeModal()
      },
    })
  }, [
    openModal,
    closeModal,
    row,
    brandKitId,
    defaultCaptionStyle,
    defaultVoiceoverVoice,
    onUpdate,
    isAvatarRow,
    videoScriptRequest,
    videoScriptToken,
    schedule.id,
  ])
  const tomorrow = dayjs().add(1, 'day').startOf('day')
  const disableDatesBefore = tomorrow.format('YYYY-MM-DD')
  const campaignEnd = dayjs(schedule.end_date)
  const disableDatesAfter = campaignEnd.isAfter(tomorrow) ? campaignEnd.format('YYYY-MM-DD') : undefined

  const renderMediaSlot = () => {
    if (isAvatarRow)
      return (
        <AvatarTile
          row={row}
          brandKitId={brandKitId}
          onUpdate={onUpdate}
          hasInRotationAvatars={hasInRotationAvatars}
          isLoading={isLoadingAvatars}
        />
      )
    if (row.isBlank) return <BlankMediaSlot row={row} onUpdate={onUpdate} />
    return <AiReferenceImage row={row} onUpdate={onUpdate} onRetry={onRetryReferenceImage} />
  }

  return (
    <div className={Styles.row}>
      <div className={Styles.mediaSlot}>{renderMediaSlot()}</div>

      <div className={cx(Styles.body, { [Styles.withContextSources]: !!row.contextSources?.length })}>
        {row.isBlank ? (
          <CaptionField
            row={row}
            onUpdate={onUpdate}
            onRegenerate={onRegenerate}
            canRemove={canRemove}
            onRemove={onRemove}
          />
        ) : (
          <TopicField
            row={row}
            schedule={schedule}
            onUpdate={onUpdate}
            onRegenerate={onRegenerate}
            onRegenerateWithContext={onRegenerateWithContext}
            canRemove={canRemove}
            onRemove={onRemove}
          />
        )}

        <div className={Styles.controlsRow}>
          <div className={Styles.controlsLeft}>
            <ContentTypeSelect
              selectedKey={CONTENT_CATEGORY_TO_CONTENT_CATEGORY_KEY[row.contentCategory]}
              isDisabled={!row.isBlank && row.loading}
              isStrictBrandControl={isStrictBrandControl}
              variant="secondary"
              size="sm"
              onSelectionChange={(key: Key) => {
                const category = CONTENT_CATEGORY_KEY_TO_CONTENT_CATEGORY[key as ContentCategoryKey]
                const config = CONTENT_CATEGORY_CONFIG[category]
                const updates: Partial<ComposeRowData> = {
                  contentCategory: category,
                  postType: config.postType,
                  tactics: getDefaultTacticsForPostType(config.postType, schedule.tactics || []),
                }
                if (category === 'avatar_video' && !row.isBlank) {
                  updates.avatar = null
                  updates.avatarAngle = 'testimonial'
                  updates.avatarScript = ''
                  updates.avatarCaptions = true
                  updates.avatarCaptionStyle = null
                  updates.productImage = null
                  updates.referenceMedia = null
                  updates.mediaFileIds = []
                  updates.referenceMediaStatus = undefined
                } else if (row.contentCategory === 'avatar_video' && category !== 'avatar_video') {
                  updates.avatar = null
                  updates.avatarScript = ''
                  updates.avatarCaptionStyle = null
                  updates.videoVoiceoverVoice = null
                  updates.productImage = null
                  updates.videoScript = ''
                }
                if (row.isBlank && row.referenceMedia) {
                  const allowed = getMediaTypesForCategory(category)
                  const currentMedia = isVideoReferenceMedia(row.referenceMedia) ? 'video' : 'image'
                  if (!allowed.includes(currentMedia)) {
                    updates.referenceMedia = null
                    updates.mediaFileIds = []
                  }
                }
                onUpdate(row.id, updates)
              }}
            />
            <Text variant="secondary" color="var(--dark-60)">
              Posting on
            </Text>
            <DateTimePicker
              dateTime={row.postAt}
              setDateTime={(dt) => {
                if (!dt) return
                onUpdate(row.id, { postAt: dt })
              }}
              disableDatesBefore={disableDatesBefore}
              disableDatesAfter={disableDatesAfter}
            >
              <Button withChevron="down" variant="secondary" size="sm">
                {row.postAt.format('MMM D, h:mma')}
              </Button>
            </DateTimePicker>
            {showScriptSettings && (
              <Button variant="secondary" size="sm" isDisabled={avatarRowDisabled} onPress={handleOpenScriptSettings}>
                Script & Settings
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

interface AvatarTileProps {
  row: ComposeRowData
  brandKitId?: number
  onUpdate: (id: string, updates: Partial<ComposeRowData>) => void
  hasInRotationAvatars: boolean
  isLoading: boolean
}

const AvatarTile = ({ row, brandKitId, onUpdate, hasInRotationAvatars, isLoading }: AvatarTileProps) => {
  const { openModal } = useModals()

  const handleChooseAvatar = () => {
    if (!brandKitId) return
    openModal(AvatarPickerModal, {
      brandKitId,
      selectedAvatarId: row.avatar?.id,
      onSelect: (avatar: BrandKitAvatar) => onUpdate(row.id, { avatar }),
    })
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleChooseAvatar()
    }
  }

  if (row.avatar?.photo_url) {
    return (
      <div
        role="button"
        tabIndex={0}
        aria-label="Change avatar"
        className={Styles.avatarTile}
        onClick={handleChooseAvatar}
        onKeyDown={handleKeyDown}
      >
        <img className={Styles.avatarPhoto} src={row.avatar.photo_url} alt={row.avatar.name} />
        <div className={Styles.avatarTileLabel}>
          <Edit3 size={16} />
          <Text variant="label" color="var(--light-100)">
            Change avatar
          </Text>
        </div>
      </div>
    )
  }

  if (!isLoading && !hasInRotationAvatars) {
    return <MissingAvatarsCallout compact />
  }

  return (
    <div
      role="button"
      tabIndex={0}
      className={Styles.uploadSlotButton}
      onClick={handleChooseAvatar}
      onKeyDown={handleKeyDown}
    >
      <Text>Choose an avatar for your video</Text>
      <Button variant="secondary" size="sm">
        Choose Avatar
      </Button>
    </div>
  )
}

interface BlankMediaSlotProps {
  row: ComposeRowData
  onUpdate: (id: string, updates: Partial<ComposeRowData>) => void
}

const BlankMediaSlot = ({ row, onUpdate }: BlankMediaSlotProps) => {
  const mediaTypes = getMediaTypesForCategory(row.contentCategory)

  const handleMediaSelected = (mediaFile: ReferenceMediaFile) => {
    onUpdate(row.id, { mediaFileIds: [mediaFile.id], referenceMedia: mediaFile })
  }

  if (row.referenceMedia?.url && (row.mediaFileIds?.length ?? 0) > 0) {
    return (
      <ReferenceImageTrigger onUse={handleMediaSelected} mediaTypes={mediaTypes} options={{ newStack: true }}>
        <ReferenceImageButton referenceMediaFile={row.referenceMedia} topicGenerated label="Change" />
      </ReferenceImageTrigger>
    )
  }

  return (
    <ReferenceImageTrigger
      onUse={handleMediaSelected}
      mediaTypes={mediaTypes}
      action="Select"
      options={{ newStack: true }}
    >
      <Pressable>
        <div role="button" tabIndex={0} className={Styles.uploadSlotButton}>
          <Upload5 size={28} />
          <Text>Add media to your post</Text>
          <Button variant="secondary" size="sm">
            Upload Media
          </Button>
        </div>
      </Pressable>
    </ReferenceImageTrigger>
  )
}

interface AiReferenceImageProps {
  row: ComposeRowData
  onUpdate: (id: string, updates: Partial<ComposeRowData>) => void
  onRetry?: (id: string) => void
}

// Pure render of the row's reference-image state. The request is owned and triggered by the
// parent (ComposeStep) at the topic-generation event, so this component can never re-fire on
// render — it only reflects row.referenceMedia / row.referenceMediaStatus.
const AiReferenceImage = ({ row, onUpdate, onRetry }: AiReferenceImageProps) => {
  const handleChangeReferenceImage = useCallback(
    (mediaFile: ReferenceMediaFile) => {
      onUpdate(row.id, { referenceMedia: mediaFile, mediaFileIds: [mediaFile.id], referenceMediaStatus: 'success' })
    },
    [row.id, onUpdate],
  )

  if (row.referenceMedia?.url) {
    return (
      <div className={Styles.imageWrapper}>
        <ReferenceImageTrigger onUse={handleChangeReferenceImage} options={{ newStack: true }}>
          <ReferenceImageButton referenceMediaFile={row.referenceMedia} topicGenerated={!!row.topic} />
        </ReferenceImageTrigger>
      </div>
    )
  }

  if (row.referenceMediaStatus === 'error') {
    return (
      <div className={Styles.imageWrapper}>
        <Text variant="label" color="var(--dark-60)">
          Error fetching reference image
        </Text>
        <div className={Styles.retryButton}>
          <Button variant="secondary" size="sm" frontIcon={ArrowRefresh2} onPress={() => onRetry?.(row.id)}>
            Retry
          </Button>
        </div>
        <div className={Styles.referenceImageButton}>
          <ReferenceImageTrigger onUse={handleChangeReferenceImage} options={{ newStack: true }}>
            <Button variant="secondary" size="sm" frontIcon={Edit3}>
              Choose manually
            </Button>
          </ReferenceImageTrigger>
        </div>
      </div>
    )
  }

  return (
    <div className={Styles.imageShimmerer}>
      <ReferenceImgLoading topicGenerated={!!row.topic} />
    </div>
  )
}

interface CaptionFieldProps {
  row: ComposeRowData
  onUpdate: (id: string, updates: Partial<ComposeRowData>) => void
  onRegenerate?: (id: string) => void
  canRemove: boolean
  onRemove: (id: string) => void
}

const CaptionField = ({ row, onUpdate, onRegenerate, canRemove, onRemove }: CaptionFieldProps) => (
  <div className={Styles.captionWrapper}>
    <div className={Styles.captionHeader}>
      <Text variant="label" color="var(--dark-60)">
        Caption
      </Text>

      <div className={Styles.controlsRight}>
        {!row.isBlank && (
          <Button
            variant="tertiary"
            size="sm"
            frontIcon={ArrowRefresh2}
            aria-label="Regenerate topic"
            isDisabled={row.loading}
            onPress={() => onRegenerate?.(row.id)}
          />
        )}

        {canRemove && (
          <Button
            variant="tertiary"
            size="sm"
            frontIcon={Trash2}
            aria-label="Delete"
            onPress={() => onRemove(row.id)}
          />
        )}
      </div>
    </div>
    <Input
      className={Styles.captionInput}
      placeholder="Add a caption or leave it blank, you can always generate it later"
      isTextarea
      name="caption"
      size="lg"
      value={row.topic}
      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
        onUpdate(row.id, { topic: e.target.value, userEdited: true })
      }
    />
  </div>
)

interface TopicFieldProps {
  row: ComposeRowData
  schedule: CampaignOption
  onUpdate: (id: string, updates: Partial<ComposeRowData>) => void
  onRegenerate?: (id: string) => void
  onRegenerateWithContext?: (id: string, contextSourceIds: number[]) => void
  canRemove: boolean
  onRemove: (id: string) => void
}

const TopicField = ({
  row,
  schedule,
  onUpdate,
  onRegenerate,
  onRegenerateWithContext,
  canRemove,
  onRemove,
}: TopicFieldProps) => {
  const [isEditingTopic, setIsEditingTopic] = useState(false)
  const { openModal } = useModals()
  const { mutate: deleteContextSource } = useDeleteFloatingContextSource()
  const { data: brandProfile } = useBrandProfile()
  const brandKitId = brandProfile?.brand_kit?.id

  const contextSources = row.contextSources

  const handleRegenerate = useCallback(() => {
    const sources = contextSources ?? []
    if (sources.length > 0) {
      onRegenerateWithContext?.(
        row.id,
        sources.map((cs) => cs.contextSourceId),
      )
    } else {
      onRegenerate?.(row.id)
    }
  }, [row.id, contextSources, onRegenerate, onRegenerateWithContext])

  const handleAddContext = useCallback(() => {
    openModal(AddContextModal, {
      scheduleId: schedule.id,
      existingSources: contextSources ?? [],
      brandKitId,
      brandKitMediaEnabled: false,
      onSubmit: (allIds: number[], allEntries: ContextSourceEntry[]) => {
        onUpdate(row.id, { contextSources: allEntries })
        onRegenerateWithContext?.(row.id, allIds)
      },
    })
  }, [openModal, schedule.id, row.id, contextSources, onUpdate, onRegenerateWithContext, brandKitId])

  const handleRemoveContextSource = useCallback(
    (contextSourceId: number) => {
      // Optimistically remove from UI
      onUpdate(row.id, {
        contextSources: (contextSources ?? []).filter((cs) => cs.contextSourceId !== contextSourceId),
      })
      // Delete from server
      deleteContextSource({ scheduleId: schedule.id, contextSourceId })
    },
    [row.id, contextSources, schedule.id, deleteContextSource, onUpdate],
  )

  if (row.loading) {
    return (
      <div className={Styles.topic}>
        <Skeleton active paragraph={{ rows: 3 }} title={false} className={Styles.topicSkeleton} />

        {!!contextSources?.length && (
          <div className={cx(Styles.contextSources, Styles.loading)}>
            {contextSources?.map((cs) => (
              <Pill key={cs.contextSourceId} size="lg" className={cx(Styles.contextSourceChip, Styles.loading)}>
                <SourceIcon source={cs.source} />
                <Text variant="secondary" color="var(--dark-90)" lineClamp={1}>
                  {getSourceLabel(cs.source).name}
                </Text>
                <IconButton isDisabled icon={Close} size="xs" variant="tertiary" />
              </Pill>
            ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className={Styles.topic}>
      <div className={Styles.topicHeader}>
        <Text variant="secondary" color="var(--dark-60)">
          Topic:
        </Text>

        <div className={Styles.controlsRight}>
          {!row.isBlank && (
            <Button
              variant="tertiary"
              size="sm"
              frontIcon={Plus}
              aria-label="Add context"
              isDisabled={row.loading}
              onPress={handleAddContext}
            >
              Add Context
            </Button>
          )}

          {!row.isBlank && (
            <Button
              variant="tertiary"
              size="sm"
              frontIcon={ArrowRefresh2}
              aria-label="Regenerate topic"
              isDisabled={row.loading}
              onPress={handleRegenerate}
            />
          )}

          {canRemove && (
            <Button
              variant="tertiary"
              size="sm"
              frontIcon={Trash2}
              aria-label="Delete"
              onPress={() => onRemove(row.id)}
            />
          )}
        </div>
      </div>
      <Input
        className={Styles.topicTextarea}
        placeholder="Enter a topic..."
        isTextarea
        withBorder={isEditingTopic}
        name="topic"
        size="lg"
        value={row.topic}
        onFocus={() => setIsEditingTopic(true)}
        onBlur={() => setIsEditingTopic(false)}
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
          onUpdate(row.id, { topic: e.target.value, userEdited: true })
        }
      />
      {!!contextSources?.length && (
        <div className={Styles.contextSources}>
          {contextSources?.map((cs) => (
            <Pill key={cs.contextSourceId} size="lg" className={Styles.contextSourceChip}>
              <SourceIcon source={cs.source} />
              <Text variant="secondary" color="var(--dark-90)" lineClamp={1}>
                {getSourceLabel(cs.source).name}
              </Text>
              <IconButton
                onPress={() => handleRemoveContextSource(cs.contextSourceId)}
                icon={Close}
                size="xs"
                variant="tertiary"
                aria-label="Remove context"
              />
            </Pill>
          ))}
        </div>
      )}
    </div>
  )
}
